import type {
  User,
  AuthResponse,
  LoginRequest,
  RegisterRequest,
  List,
  CreateListRequest,
  UpdateListRequest,
  ListItem,
  CreateListItemRequest,
  UpdateListItemRequest,
  ApiResponse,
  ApiError,
  ListMember,
  ListInvite,
  ShareListRequest,
  AddListMemberRequest,
  SocialAuthRequest,
  Recipe,
  CreateRecipeRequest,
  UpdateRecipeRequest,
  ImportRecipeRequest,
} from '@cleverlist/types';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private onUnauthorized?: () => void;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
  }

  setToken(token: string | null) {
    this.token = token;
  }

  setOnUnauthorized(callback: () => void) {
    this.onUnauthorized = callback;
  }

  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {} } = options;

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...headers,
    };

    if (this.token) {
      requestHeaders['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401) {
      this.onUnauthorized?.();
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));
      throw error;
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  private async get<T>(endpoint: string): Promise<T> {
    const response = await this.request<ApiResponse<T>>(endpoint);
    return response.data;
  }

  private async post<T>(endpoint: string, body?: unknown): Promise<T> {
    const response = await this.request<ApiResponse<T>>(endpoint, {
      method: 'POST',
      body,
    });
    return response.data;
  }

  private async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    const response = await this.request<ApiResponse<T>>(endpoint, {
      method: 'PATCH',
      body,
    });
    return response.data;
  }

  private async delete<T>(endpoint: string): Promise<T> {
    const response = await this.request<ApiResponse<T>>(endpoint, {
      method: 'DELETE',
    });
    return response.data;
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    return this.post<AuthResponse>('/api/v1/auth/login', credentials);
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    return this.post<AuthResponse>('/api/v1/auth/register', data);
  }

  async logout(): Promise<void> {
    await this.request('/api/v1/auth/logout', { method: 'POST' });
  }

  async getUser(): Promise<User> {
    return this.get<User>('/api/v1/auth/user');
  }

  async createGuestSession(deviceId: string): Promise<AuthResponse> {
    return this.post<AuthResponse>('/api/v1/auth/guest', { device_id: deviceId });
  }

  // Social Auth
  async socialLogin(provider: 'google' | 'apple', data: SocialAuthRequest): Promise<AuthResponse> {
    return this.post<AuthResponse>(`/api/v1/auth/social/${provider}`, data);
  }

  // List endpoints
  async getLists(): Promise<List[]> {
    return this.get<List[]>('/api/v1/lists');
  }

  async getList(id: number): Promise<List> {
    return this.get<List>(`/api/v1/lists/${id}`);
  }

  async createList(data: CreateListRequest): Promise<List> {
    return this.post<List>('/api/v1/lists', data);
  }

  async updateList(id: number, data: UpdateListRequest): Promise<List> {
    return this.patch<List>(`/api/v1/lists/${id}`, data);
  }

  async deleteList(id: number): Promise<void> {
    await this.request(`/api/v1/lists/${id}`, { method: 'DELETE' });
  }

  // List Item endpoints
  async getListItems(listId: number): Promise<ListItem[]> {
    return this.get<ListItem[]>(`/api/v1/lists/${listId}/items`);
  }

  async createListItem(listId: number, data: CreateListItemRequest): Promise<ListItem> {
    return this.post<ListItem>(`/api/v1/lists/${listId}/items`, data);
  }

  async updateListItem(itemId: number, data: UpdateListItemRequest): Promise<ListItem> {
    return this.patch<ListItem>(`/api/v1/items/${itemId}`, data);
  }

  async deleteListItem(itemId: number): Promise<void> {
    await this.request(`/api/v1/items/${itemId}`, { method: 'DELETE' });
  }

  async reorderListItems(listId: number, itemIds: number[]): Promise<void> {
    await this.request(`/api/v1/lists/${listId}/reorder`, {
      method: 'POST',
      body: { item_ids: itemIds },
    });
  }

  // List Members & Sharing
  async getListMembers(listId: number): Promise<ListMember[]> {
    return this.get<ListMember[]>(`/api/v1/lists/${listId}/members`);
  }

  async addListMember(listId: number, data: AddListMemberRequest): Promise<ListMember[]> {
    return this.post<ListMember[]>(`/api/v1/lists/${listId}/members`, data);
  }

  async removeListMember(listId: number, userId: number): Promise<void> {
    await this.request(`/api/v1/lists/${listId}/members/${userId}`, { method: 'DELETE' });
  }

  async shareList(listId: number, data: ShareListRequest): Promise<ListInvite> {
    return this.post<ListInvite>(`/api/v1/lists/${listId}/share`, data);
  }

  async joinList(code: string): Promise<List> {
    return this.post<List>(`/api/v1/lists/join/${code}`);
  }

  // Recipe endpoints
  async getRecipe(id: number): Promise<Recipe> {
    return this.get<Recipe>(`/api/v1/recipes/${id}`);
  }

  async createRecipe(data: CreateRecipeRequest): Promise<Recipe> {
    return this.post<Recipe>('/api/v1/recipes', data);
  }

  async updateRecipe(id: number, data: UpdateRecipeRequest): Promise<Recipe> {
    return this.patch<Recipe>(`/api/v1/recipes/${id}`, data);
  }

  async deleteRecipe(id: number): Promise<void> {
    await this.request(`/api/v1/recipes/${id}`, { method: 'DELETE' });
  }

  async importRecipe(data: ImportRecipeRequest): Promise<Recipe> {
    return this.post<Recipe>('/api/v1/recipes/import', data);
  }
}

// Create and export a singleton instance
export const createApiClient = (baseUrl: string) => new ApiClient(baseUrl);

export type { ApiClient };
export default ApiClient;
